import React, { useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import { FaDownload, FaImage, FaLink, FaQrcode, FaTimes } from "react-icons/fa";

const CANVAS_SIZE = 320;
const SVG_NS = "http://www.w3.org/2000/svg";
const XLINK_NS = "http://www.w3.org/1999/xlink";

const inputClass =
  "w-full rounded-xl border border-[var(--border)] bg-[var(--surface-soft)] px-4 py-3 text-sm text-[var(--text)] outline-none transition-all duration-300 placeholder:text-[#94A3B8]/60 focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand-soft)]";

const buttonClass =
  "inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--brand-soft)]";

const buildDownloadBaseName = (link) => {
  if (!link) {
    return "qr-code";
  }

  const slug = link
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);

  return slug ? `qr-code-${slug}` : "qr-code";
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const injectCenterImageIntoSvg = (svgString, imageDataUrl) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, "image/svg+xml");
  const svg = doc.documentElement;

  if (!svg) {
    return svgString;
  }

  const size = CANVAS_SIZE;
  const padding = Math.round(size * 0.06);
  const imageSize = Math.round(size * 0.24);
  const x = Math.round((size - imageSize) / 2);
  const y = Math.round((size - imageSize) / 2);
  const boxX = x - padding;
  const boxY = y - padding;
  const boxSize = imageSize + padding * 2;
  const boxRadius = Math.max(16, Math.round(boxSize * 0.18));
  const imageRadius = Math.max(12, Math.round(imageSize * 0.18));
  const clipId = "qr-center-image-clip";

  if (!svg.getAttribute("xmlns:xlink")) {
    svg.setAttribute("xmlns:xlink", XLINK_NS);
  }

  const defs = doc.createElementNS(SVG_NS, "defs");
  const clipPath = doc.createElementNS(SVG_NS, "clipPath");
  clipPath.setAttribute("id", clipId);

  const clipRect = doc.createElementNS(SVG_NS, "rect");
  clipRect.setAttribute("x", String(x));
  clipRect.setAttribute("y", String(y));
  clipRect.setAttribute("width", String(imageSize));
  clipRect.setAttribute("height", String(imageSize));
  clipRect.setAttribute("rx", String(imageRadius));
  clipRect.setAttribute("ry", String(imageRadius));
  clipPath.appendChild(clipRect);
  defs.appendChild(clipPath);
  svg.appendChild(defs);

  const background = doc.createElementNS(SVG_NS, "rect");
  background.setAttribute("x", String(boxX));
  background.setAttribute("y", String(boxY));
  background.setAttribute("width", String(boxSize));
  background.setAttribute("height", String(boxSize));
  background.setAttribute("rx", String(boxRadius));
  background.setAttribute("ry", String(boxRadius));
  background.setAttribute("fill", "#ffffff");
  background.setAttribute("stroke", "#e5e7eb");
  background.setAttribute("stroke-width", "2");
  svg.appendChild(background);

  const image = doc.createElementNS(SVG_NS, "image");
  image.setAttribute("x", String(x));
  image.setAttribute("y", String(y));
  image.setAttribute("width", String(imageSize));
  image.setAttribute("height", String(imageSize));
  image.setAttribute("clip-path", `url(#${clipId})`);
  image.setAttribute("href", imageDataUrl);
  image.setAttributeNS(XLINK_NS, "xlink:href", imageDataUrl);
  svg.appendChild(image);

  return new XMLSerializer().serializeToString(doc);
};

const QrCodeGenerator = () => {
  const [linkInput, setLinkInput] = useState("");
  const [generatedLink, setGeneratedLink] = useState("");
  const [centerImageSrc, setCenterImageSrc] = useState("");
  const [centerImageName, setCenterImageName] = useState("");
  const [message, setMessage] = useState("Paste a link and generate a QR code.");
  const [error, setError] = useState("");
  const [isRendering, setIsRendering] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!generatedLink || !canvasRef.current) {
      return;
    }

    let isActive = true;

    const loadImage = (src) =>
      new Promise((resolve, reject) => {
        const image = new window.Image();
        image.crossOrigin = "anonymous";
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
      });

    const renderQrCode = async () => {
      setIsRendering(true);
      try {
        await QRCode.toCanvas(canvasRef.current, generatedLink, {
          errorCorrectionLevel: centerImageSrc ? "H" : "M",
          margin: 2,
          width: CANVAS_SIZE,
          color: {
            dark: "#111111",
            light: "#ffffff",
          },
        });

        if (centerImageSrc) {
          const image = await loadImage(centerImageSrc);
          if (!isActive || !canvasRef.current) {
            return;
          }

          const canvas = canvasRef.current;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            throw new Error("Unable to access canvas context.");
          }

          const size = canvas.width;
          const padding = Math.round(size * 0.06);
          const imageSize = Math.round(size * 0.24);
          const x = Math.round((size - imageSize) / 2);
          const y = Math.round((size - imageSize) / 2);
          const radius = Math.max(12, Math.round(imageSize * 0.18));

          ctx.save();
          ctx.fillStyle = "#ffffff";
          ctx.strokeStyle = "#e5e7eb";
          ctx.lineWidth = 2;
          ctx.beginPath();
          const boxX = x - padding;
          const boxY = y - padding;
          const boxSize = imageSize + padding * 2;
          const boxRadius = Math.max(16, Math.round(boxSize * 0.18));

          ctx.moveTo(boxX + boxRadius, boxY);
          ctx.arcTo(boxX + boxSize, boxY, boxX + boxSize, boxY + boxSize, boxRadius);
          ctx.arcTo(boxX + boxSize, boxY + boxSize, boxX, boxY + boxSize, boxRadius);
          ctx.arcTo(boxX, boxY + boxSize, boxX, boxY, boxRadius);
          ctx.arcTo(boxX, boxY, boxX + boxSize, boxY, boxRadius);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(x + radius, y);
          ctx.arcTo(x + imageSize, y, x + imageSize, y + imageSize, radius);
          ctx.arcTo(x + imageSize, y + imageSize, x, y + imageSize, radius);
          ctx.arcTo(x, y + imageSize, x, y, radius);
          ctx.arcTo(x, y, x + imageSize, y, radius);
          ctx.closePath();
          ctx.clip();
          ctx.drawImage(image, x, y, imageSize, imageSize);
          ctx.restore();
        }

        if (isActive) {
          setError("");
          setMessage("QR code ready. Download it as JPG, PNG, or SVG, or update the link or image to generate another one.");
        }
      } catch (renderError) {
        console.error("QR code render failed", renderError);
        if (isActive) {
          setError("We couldn't generate the QR code. Please check the link or image and try again.");
          setMessage("The QR preview could not be rendered.");
        }
      } finally {
        if (isActive) {
          setIsRendering(false);
        }
      }
    };

    renderQrCode();

    return () => {
      isActive = false;
    };
  }, [generatedLink, centerImageSrc]);

  const downloadName = useMemo(() => {
    if (!generatedLink) {
      return "qr-code";
    }

    return buildDownloadBaseName(generatedLink);
  }, [generatedLink]);

  const handleGenerate = (event) => {
    event.preventDefault();

    const nextLink = linkInput.trim();
    if (!nextLink) {
      setError("Please paste a link first.");
      setMessage("A valid link is required before generating a QR code.");
      return;
    }

    setError("");
    setGeneratedLink(nextLink);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file for the QR center image.");
      setMessage("Upload a JPG, PNG, WebP, or similar image.");
      event.target.value = "";
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setError("");
    setCenterImageSrc((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }
      return objectUrl;
    });
    setCenterImageName(file.name);
    setMessage("Image added. Generate the QR code to place it in the center.");
  };

  const handleDownload = async (format) => {
    if (!generatedLink) {
      return;
    }

    try {
      if (format === "svg") {
        const svgString = await QRCode.toString(generatedLink, {
          type: "svg",
          errorCorrectionLevel: centerImageSrc ? "H" : "M",
          margin: 2,
          width: CANVAS_SIZE,
          color: {
            dark: "#111111",
            light: "#ffffff",
          },
        });

        const finalSvg = centerImageSrc
          ? injectCenterImageIntoSvg(svgString, await blobToDataUrl(await (await fetch(centerImageSrc)).blob()))
          : svgString;

        downloadBlob(
          new Blob([finalSvg], { type: "image/svg+xml;charset=utf-8" }),
          `${downloadName}.svg`
        );
        return;
      }

      if (!canvasRef.current) {
        return;
      }

      const isPng = format === "png";
      const mimeType = isPng ? "image/png" : "image/jpeg";
      const extension = isPng ? "png" : "jpg";
      const downloadUrl = isPng
        ? canvasRef.current.toDataURL(mimeType)
        : canvasRef.current.toDataURL(mimeType, 1);

      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = `${downloadName}.${extension}`;
      anchor.style.display = "none";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch (downloadError) {
      console.error("QR code download failed", downloadError);
      setError("We couldn't download the QR code. Please try again.");
      setMessage("Download failed. Please try another format.");
    }
  };

  const handleReset = () => {
    setLinkInput("");
    setGeneratedLink("");
    if (centerImageSrc) {
      URL.revokeObjectURL(centerImageSrc);
    }
    setCenterImageSrc("");
    setCenterImageName("");
    setError("");
    setMessage("Paste a link and generate a QR code.");
  };

  return (
    <main className="home-shell min-h-screen pt-16">
      <section className="home-section border-none pt-10 sm:pt-14">
        <div className="home-container px-4 sm:px-6">
          <div className="relative overflow-hidden rounded-[2rem] border border-[var(--border)] bg-[color-mix(in_srgb,var(--surface-strong)_88%,transparent)] shadow-[0_24px_60px_-34px_rgba(0,0,0,0.4)]">
            <div className="absolute -left-14 top-8 h-40 w-40 rounded-full bg-[color-mix(in_srgb,var(--brand)_18%,transparent)] blur-3xl" />
            <div className="absolute bottom-0 right-0 h-48 w-48 rounded-full bg-[color-mix(in_srgb,var(--brand)_12%,transparent)] blur-3xl" />

            <div className="relative grid gap-8 px-5 py-8 sm:px-8 sm:py-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-start lg:gap-10 lg:px-10 lg:py-12">
              <div className="space-y-5">
                <span className="section-kicker">
                  <FaQrcode />
                  QR Generator
                </span>

                <div className="space-y-4">
                  <h1 className="home-title text-3xl font-bold sm:text-4xl lg:text-5xl">
                    Turn any link into a <span className="home-accent-gradient">shareable QR code</span>
                  </h1>
                  <p className="max-w-2xl text-sm leading-7 text-[var(--muted)] sm:text-base">
                    Paste a URL, generate the code, and download it as JPG, PNG, or SVG for posters, slides, handouts, or quick sharing.
                  </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <FaLink className="text-[var(--brand)]" />
                      Link input
                    </div>
                    <p className="mt-2 text-sm text-[var(--muted)]">Paste the destination URL you want to share.</p>
                  </div>
                  <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <FaDownload className="text-[var(--brand)]" />
                      Download formats
                    </div>
                    <p className="mt-2 text-sm text-[var(--muted)]">Save the generated code as JPG, PNG, or SVG.</p>
                  </div>
                </div>

                <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-soft)] p-4 text-sm text-[var(--muted)]">
                  Tip: links with `https://` work best, but the generator will encode whatever text you paste.
                </div>
              </div>

              <div className="home-card-strong p-5 sm:p-6">
                <form className="space-y-4" onSubmit={handleGenerate}>
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold">Paste link</span>
                    <input
                      className={inputClass}
                      type="text"
                      inputMode="url"
                      placeholder="https://example.com"
                      value={linkInput}
                      onChange={(event) => setLinkInput(event.target.value)}
                    />
                  </label>

                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold">Center image (optional)</span>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                      <label className={`${buttonClass} home-btn-secondary cursor-pointer px-4 py-3`}>
                        <FaImage />
                        Upload image
                        <input className="hidden" type="file" accept="image/*" onChange={handleImageUpload} />
                      </label>
                      {centerImageName ? (
                        <div className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded-xl border border-[var(--border)] bg-[var(--surface-soft)] px-4 py-3 text-sm">
                          <span className="truncate text-[var(--muted)]">{centerImageName}</span>
                          <button
                            type="button"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[var(--border)] text-[var(--muted)] transition hover:border-[var(--brand)] hover:text-[var(--text)]"
                            onClick={() => {
                              if (centerImageSrc) {
                                URL.revokeObjectURL(centerImageSrc);
                              }
                              setCenterImageSrc("");
                              setCenterImageName("");
                              setMessage("Center image removed.");
                            }}
                            aria-label="Remove center image"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-dashed border-[var(--border)] bg-[var(--surface-soft)] px-4 py-3 text-sm text-[var(--muted)] sm:flex-1">
                          Optional image that will appear in the middle of the QR code.
                        </div>
                      )}
                    </div>
                  </label>

                  <div className="flex flex-col gap-3 sm:flex-row">
                    <button type="submit" className={`${buttonClass} home-btn-primary flex-1 px-5 py-3`}>
                      <FaQrcode />
                      Generate QR
                    </button>
                    <button type="button" className={`${buttonClass} home-btn-secondary px-5 py-3`} onClick={handleReset}>
                      Reset
                    </button>
                  </div>
                </form>

                <div className="mt-5 rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4 sm:p-5">
                  {generatedLink ? (
                    <div className="space-y-4">
                      <div className="overflow-hidden rounded-2xl border border-[var(--border)] bg-white p-4 shadow-sm">
                        <canvas ref={canvasRef} className="mx-auto block h-auto w-full max-w-[320px]" />
                      </div>

                      <div className="space-y-2">
                        <p className="text-sm font-semibold">Current link</p>
                        <p className="break-words text-sm text-[var(--muted)]">{generatedLink}</p>
                      </div>

                      {centerImageName ? (
                        <div className="space-y-2">
                          <p className="text-sm font-semibold">Center image</p>
                          <p className="text-sm text-[var(--muted)]">{centerImageName}</p>
                        </div>
                      ) : null}

                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-sm text-[var(--muted)]">{isRendering ? "Generating QR code..." : message}</div>
                        <div className="grid overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--surface-soft)] sm:grid-cols-3">
                          <button
                            type="button"
                            onClick={() => handleDownload("jpg")}
                            disabled={isRendering}
                            className="inline-flex items-center justify-center gap-2 border-b border-[var(--border)] px-4 py-3 text-sm font-semibold transition hover:bg-[var(--surface)] disabled:cursor-not-allowed disabled:opacity-60 sm:border-b-0 sm:border-r"
                          >
                            <FaDownload />
                            JPG
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDownload("png")}
                            disabled={isRendering}
                            className="inline-flex items-center justify-center gap-2 border-b border-[var(--border)] px-4 py-3 text-sm font-semibold transition hover:bg-[var(--surface)] disabled:cursor-not-allowed disabled:opacity-60 sm:border-b-0 sm:border-r"
                          >
                            <FaDownload />
                            PNG
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDownload("svg")}
                            disabled={isRendering}
                            className="inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold transition hover:bg-[var(--surface)] disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <FaDownload />
                            SVG
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--border)] bg-[var(--surface-soft)] px-6 py-10 text-center">
                      <FaQrcode className="text-3xl text-[var(--brand)]" />
                      <p className="mt-4 text-base font-semibold">Your QR preview will appear here</p>
                      <p className="mt-2 max-w-sm text-sm leading-6 text-[var(--muted)]">{message}</p>
                    </div>
                  )}

                  {error ? <p className="mt-4 rounded-xl border border-rose-500/20 bg-rose-500/5 px-4 py-3 text-sm text-rose-600 dark:text-rose-400">{error}</p> : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default QrCodeGenerator;
